import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Flame, Loader2, CheckCircle2, Users } from "lucide-react";
import { PUBLIC_ORIGIN } from "../lib/domain";

function ShareRow() {
  const url = typeof window !== "undefined" ? `${window.location.origin}/defis` : `${PUBLIC_ORIGIN}/defis`;
  const enc = encodeURIComponent(url);
  const qr = `https://api.qrserver.com/v1/create-qr-code/?data=${enc}&size=150x150&bgcolor=10-9-8&color=201-169-97`;
  const copy = () => { navigator.clipboard?.writeText(url); toast.success("Lien copié ! Colle-le dans ta bio ou ta Story Instagram"); };
  return (
    <div className="mt-8 flex flex-col items-center gap-3" data-testid="defis-share">
      <div className="kz-mono text-[10px] tracking-[0.2em] uppercase text-white/40">Partager le défi</div>
      <div className="flex items-center gap-3">
        <a href={`https://www.facebook.com/sharer/sharer.php?u=${enc}`} target="_blank" rel="noreferrer" data-testid="share-facebook"
          className="flex items-center gap-2 bg-[#1877F2] hover:opacity-90 text-white text-sm font-semibold px-4 py-2 rounded-lg">
          Facebook
        </a>
        <button onClick={copy} data-testid="share-instagram"
          className="flex items-center gap-2 bg-gradient-to-r from-[#F58529] via-[#DD2A7B] to-[#8134AF] hover:opacity-90 text-white text-sm font-semibold px-4 py-2 rounded-lg">
          Instagram (copier le lien)
        </button>
      </div>
      <img src={qr} alt="QR code du défi" width={130} height={130}
        className="mt-2 rounded-lg border border-white/10 bg-[#0A0908] p-1" data-testid="defis-qr" />
      <div className="text-[10px] text-white/40">Scanne pour t'inscrire</div>
    </div>
  );
}

export default function DefisPublic() {
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/public/kaizen-challenges")
      .then((r) => setChallenges(r.data.challenges || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0908] text-white" data-testid="defis-public">
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 py-20 text-center">
          <div className="inline-flex items-center gap-2 text-[#C9A961] kz-mono text-xs tracking-[0.2em] uppercase mb-4">
            <Flame className="w-4 h-4" /> Défis Kaizen · Transformations
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            21 jours pour <span className="text-[#D90429]">avancer.</span>
          </h1>
          <p className="mt-4 text-white/70 max-w-2xl mx-auto">
            Bouger. Manger mieux. S'entraîner. Progresser. Ouvert aux membres et à la communauté — à ton rythme, sans promesse miracle.
          </p>
          <ShareRow />
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-14">
        {loading ? (
          <Loader2 className="w-6 h-6 animate-spin text-[#C9A961] mx-auto" />
        ) : challenges.length === 0 ? (
          <p className="text-center text-white/50 py-16" data-testid="defis-public-empty">
            Aucun défi ouvert pour le moment. Reviens bientôt ! 🔥
          </p>
        ) : (
          <div className="space-y-6" data-testid="defis-public-list">
            {challenges.map((c) => <ChallengeBlock key={c.id} c={c} />)}
          </div>
        )}
      </section>
    </div>
  );
}

function ChallengeBlock({ c }) {
  const [form, setForm] = useState({ nom: "", courriel: "", telephone: "", niveau: "debutant" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const join = async (e) => {
    e.preventDefault();
    if (!form.nom.trim() || !form.courriel.includes("@")) { toast.error("Nom et courriel requis"); return; }
    setBusy(true);
    try {
      await api.post(`/public/kaizen-challenges/${c.id}/join`, form);
      setDone(true);
      toast.success("🔥 Inscription confirmée !");
    } catch (e2) { toast.error(e2.response?.data?.detail || "Erreur"); }
    setBusy(false);
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden" data-testid={`defi-public-${c.id}`}>
      <div className="p-6 md:p-8 grid md:grid-cols-2 gap-6">
        <div>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#D90429]/20 text-[#ff6b81] uppercase tracking-wide">
            {c.status === "actif" ? "En cours" : "À venir"}
          </span>
          <h2 className="text-2xl font-black mt-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{c.nom}</h2>
          <p className="text-white/60 text-sm">{c.sous_titre}</p>
          <p className="text-[#C9A961] text-sm mt-2">Départ : {c.date_debut} · {c.duree_jours} jours</p>
          {c.objectif && <p className="text-white/50 text-xs mt-3 leading-relaxed">{c.objectif}</p>}
          {c.piliers && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {c.piliers.map((p) => (
                <span key={p.key} className="text-[10px] bg-white/5 border border-white/10 rounded-full px-2 py-0.5 text-white/60">{p.nom}</span>
              ))}
            </div>
          )}
          {c.affiche_url && <img src={c.affiche_url} alt="Affiche" className="mt-4 w-36 rounded-lg border border-white/10" data-testid="defi-public-affiche" />}
        </div>
        <div>
          {done ? (
            <div className="h-full flex flex-col items-center justify-center text-center gap-2" data-testid="defi-join-done">
              <CheckCircle2 className="w-10 h-10 text-emerald-400" />
              <p className="font-bold">Tu es inscrit(e) ! 🎉</p>
              <p className="text-white/60 text-sm">On t'envoie tous les détails par courriel. Prépare-toi à bouger !</p>
            </div>
          ) : (
            <form onSubmit={join} className="space-y-2.5" data-testid="defi-join-form">
              <input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} placeholder="Ton nom" data-testid="defi-join-nom"
                className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2.5 text-sm" />
              <input value={form.courriel} onChange={(e) => setForm({ ...form, courriel: e.target.value })} placeholder="Ton courriel" type="email" data-testid="defi-join-courriel"
                className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2.5 text-sm" />
              <input value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} placeholder="Téléphone (optionnel)" data-testid="defi-join-tel"
                className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2.5 text-sm" />
              <select value={form.niveau} onChange={(e) => setForm({ ...form, niveau: e.target.value })} data-testid="defi-join-niveau"
                className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2.5 text-sm">
                <option value="debutant">Débutant</option>
                <option value="intermediaire">Intermédiaire</option>
                <option value="athlete">Athlète</option>
              </select>
              <button type="submit" disabled={busy} data-testid="defi-join-submit"
                className="w-full flex items-center justify-center gap-2 bg-[#D90429] hover:bg-[#b8031f] disabled:opacity-50 text-white font-bold px-4 py-3 rounded-lg text-sm uppercase tracking-wide">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flame className="w-4 h-4" />} Je relève le défi
              </button>
              <p className="text-[10px] text-white/40 text-center">Aucune promesse médicale. Adapté à ton niveau. Consulte un pro de la santé au besoin.</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
